import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

/* Images */
import placeholder from '~/../img/vehicle-placeholder.png';

/* Common */
import CardProduct from '~/components/common/cards/CardProduct';
import CardVehicleUnavailable from '~/components/common/cards/CardVehicleUnavailable';
import Button from '~/components/common/Button';

/* Utilities */
import { trans, getSuffix, validateVehicleExistence } from '~/utilities';

/* Config */
import { reactToastifyConfig } from '~/config';

/* Custom */
import eventBus, { EVENTS } from '~/custom/eventBus';

export default function FavoritesOverview({ template, alerts, removeFavoriteUrl, getFavoritesUrl, vehicleExistsUrl, setPriceAlertsUrl, togglePriceAlertsUrl }) {
    const [favoritesList, setFavoritesList] = useState([]);
    const [email, setEmail] = useState('');

    const fetchFavorites = async () => {
        try {
            const response = await axios.get(getFavoritesUrl);
            const fetchedFavorites = response.data.vehicles;
            if (fetchedFavorites && Array.isArray(fetchedFavorites)) {
                const validatedFavorites = await validateVehicleExistence(fetchedFavorites, vehicleExistsUrl);
                setFavoritesList(validatedFavorites);
            } else {
                toast.error('Er is iets verkeerd gegaan bij het ophalen van favorieten.');
            }
        } catch (error) {
            toast.error('Er is iets verkeerd gegaan, probeer het nogmaals.');
        }
    };

    useEffect(() => {
        toast.configure(reactToastifyConfig);
        fetchFavorites();
    }, []);

    const handleFavoriteRemoved = (vehicleId) => {
        setFavoritesList((prevFavorites) => prevFavorites.filter((vehicle) => vehicle.id !== vehicleId));
        eventBus.emit(EVENTS.FAVORITES_UPDATED);
    };

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handleSetPriceAlerts = async () => {
        try {
            const response = await axios.post(setPriceAlertsUrl, { email });

            if (response.data.success) {
                toast.success('Price alerts ingesteld.');
            } else {
                toast.error('Er is iets verkeerd gegaan, probeer het nogmaals.');
            }
        } catch (error) {
            toast.error('Er is iets verkeerd gegaan, probeer het nogmaals.');
        }
    };

    const togglePriceAlerts = async () => {
        try {
            const response = await axios.post(togglePriceAlertsUrl);
            if (response.data.success) {
                toast.success('Prijsmeldingen succesvol aangepast.');
            } else {
                toast.error('Er is iets misgegaan bij het instellen van prijsmeldingen.');
            }
        } catch (error) {
            toast.error('Er is iets misgegaan bij het instellen van prijsmeldingen.');
        }
    };

    return (
        <div className="favorites-overview row row--equalized gutters-0 gutters-md-4 overflow-hidden">
            <div className="col-12 col-md-6 col-lg-4 col-xl-3">
                <div className="card card--favorites-alert">
                    <div className="card-body p-8">
                        <p className="text--18 font-weight-bold mb-0 text-base">{trans('app.favorites.favorites_overview.card.title')}</p>
                        <p>{trans('app.favorites.favorites_overview.card.text')}</p>
                        <form onSubmit={(e) => { e.preventDefault(); handleSetPriceAlerts(); }}>
                            {template === 'extranet' ? (
                                <button
                                    type="button"
                                    className={`btn btn-lg btn-toggle ${alerts ? 'active' : ''}`}
                                    data-toggle="button"
                                    aria-pressed={alerts ? 'true' : 'false'}
                                    onClick={() => {
                                        togglePriceAlerts();
                                    }}
                                >
                                    <div className="handle"></div>
                                </button>

                            ) : (
                                <>
                                    <div className="form-group--flow form-group--flow-field-email form-group--flow-value form-group">
                                        <label className="form-label" htmlFor="email">E-mailadres</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            required
                                        />
                                    </div>
                                    <Button
                                        theme="primary"
                                        title={trans('app.favorites.favorites_overview.card.btn_text')}
                                        className="btn-block"
                                    />
                                </>
                            )}
                        </form>
                    </div>
                </div>
            </div>

            {favoritesList.map(vehicle => {

                if (vehicle.exists === false) {
                    return (
                        <div key={vehicle.id} className="col-12 col-md-6 col-lg-4 col-xl-3">
                            <CardVehicleUnavailable
                                id={vehicle.id}
                                title={vehicle.brand || vehicle.model ? `${vehicle.brand || ''} ${vehicle.model || ''}`.trim() : ''}
                                subtitle={vehicle.type || ''}
                                onFavoriteRemoved={handleFavoriteRemoved}
                                removeFavoriteUrl={removeFavoriteUrl}
                            />
                        </div>
                    );
                }

                const vehicleSpecs = ['bouwjaar', 'tellerstand', 'brandstof'];

                const specs = vehicleSpecs.map((spec) => {
                    const attribute = vehicle.attributes[spec];
                    if (typeof attribute !== 'undefined') {
                        if (attribute.code === 'tellerstand') {
                            return `${attribute.value.toLocaleString('nl-BE')} km`;
                        }

                        if (attribute.code === 'actieradius_elektrisch') return `${attribute.value} ${getSuffix(attribute.code)} actieradius`;
                        if (attribute.code === 'accu_capaciteit') return `${attribute.value} ${getSuffix(attribute.code)}`;

                        return `${attribute.value} ${getSuffix(attribute.code)}`;
                    }
                    return null;
                }).filter((spec) => spec !== null);

                return (
                    <div key={vehicle.id} className="col-12 col-md-6 col-lg-4 col-xl-3">
                        <CardProduct
                            title={`${vehicle?.brand} ${vehicle?.model}`}
                            subtitle={`${vehicle?.type} ${vehicle?.attributes?.toevoeging?.value || ''}`}
                            model={vehicle?.model}
                            images={vehicle?.images?.map((image) => ({
                                src: image?.path || `${window.location.origin}${placeholder}`,
                                alt: image?.description || `${vehicle?.brand} ${vehicle?.model} ${vehicle?.type}`,
                            }))}
                            location={vehicle.enrichedValues?.vestiging_naam}
                            specs={specs}
                            price={
                                {
                                    title: trans('app.vehicle_prices.price.title'),
                                    value: vehicle?.price,
                                    discount: vehicle?.attributes.actieprijs.value,
                                    type: 'price'
                                }
                            }
                            lease={
                                {
                                    title: trans('app.vehicle_prices.finance_price.title'),
                                    value: vehicle?.enrichedValues.finance_price,
                                    type: 'finance_price'
                                }
                            }
                            privateLease={
                                {
                                    title: trans('app.vehicle_prices.private_lease.title'),
                                    value: vehicle?.enrichedValues.private_lease,
                                    type: 'private_lease'
                                }
                            }
                            href={vehicle.url}
                            vehicleType={vehicle.attributes?.voertuigsoort?.value}
                            btwMarge={vehicle.attributes?.btw_marge?.value}
                            availability={vehicle.enrichedValues?.availability}
                            id={vehicle.id}
                            favoritesOverview={true}
                            onFavoriteRemoved={handleFavoriteRemoved}
                            removeFavoriteUrl={removeFavoriteUrl}
                        />
                    </div>
                );
            })}
        </div>
    );
}
